import { useMemo } from "react";
import {
	createSrcsetUrlTemplate,
	generateWidths,
	getDimensionFromUrl,
} from "../image.utils";
import { isNumber } from "@sindresorhus/is";

const CONFIG = {
	maxWidth: 1800,
	minWidth: 320,
	formats: ["avif", "webp"] as const,
} as const;

type ImageFormat = "avif" | "webp" | "jpg" | "png";

interface StoryblokImageConfig {
	src: string;
	width?: number;
	height?: number;
	quality?: number;
	tolerance?: number;
	aspectRatio?: number;
}

const getOriginalFormat = (url: string): ImageFormat => {
	const match = url.toLowerCase().match(/\.(jpe?g|png|webp|avif)$/i);
	const format = match?.[1];

	if (!format) return "jpg"; // default fallback

	// normalize jpeg/jpg to jpg
	if (format === "jpeg") return "jpg";
	return format as ImageFormat;
};

const cleanImageUrl = (url: string): string => {
	const match = url.match(/(.*?\.(?:jpg|jpeg|png|webp|avif))/i);
	return match?.[1] ?? url;
};

export const useStoryblokImage = ({
	src,
	width,
	height,
	quality = 80,
	tolerance,
	aspectRatio,
}: StoryblokImageConfig) => {
	return useMemo(() => {
		const cleanedSrc = cleanImageUrl(src);
		const originalFormat = getOriginalFormat(cleanedSrc);

		const dimensions = getDimensionFromUrl(cleanedSrc);
		const [imageMaxWidth, imageMaxHeight] = dimensions;
		const ratio = isNumber(aspectRatio)
			? aspectRatio
			: imageMaxHeight / imageMaxWidth;

		const minW = width ? width / 2 : CONFIG.minWidth;
		const maxW = width ? width * 2 : CONFIG.maxWidth;
		const widths = generateWidths(
			Math.min(CONFIG.minWidth, minW),
			maxW,
			tolerance,
		);

		const imageHeight =
			height ?? (width ?? (imageMaxWidth - widths[0]) / 2) * ratio;
		const imageWidth = width ?? (imageMaxWidth - widths[0]) / 2;

		const createSourceUrl = (format: ImageFormat) => {
			const srcSets = widths.map(
				createSrcsetUrlTemplate({
					src: cleanedSrc,
					aspectRatio: ratio,
					filters: `filters:format(${format}):quality(${quality})`,
				}),
			);
			return srcSets.join(",");
		};

		const placeholderSrc = `${src}/m/40x${Math.round(
			40 * ratio,
		)}/smart/filters:quality(50):blur(2)`;

		return {
			imageWidth,
			imageHeight,
			ratio,
			originalFormat,
			placeholderSrc,
			createSourceUrl,
			formats: CONFIG.formats,
		};
	}, [src, aspectRatio, width, tolerance, height, quality]);
};
